"use client";

import { useState } from "react";
import type { ConsentState, CookieCategory } from "./cookie-consent.types";
import { useCookieConsent } from "./useCookieConsent";
import { CookieConsentSettings } from "./CookieConsentSettings";

export interface CookieConsentLabels {
  title: string;
  description: string;
  acceptAll: string;
  rejectAll: string;
  customize: string;
  settings: {
    necessary: string;
    necessaryDesc: string;
    analytics: string;
    analyticsDesc: string;
    functional: string;
    functionalDesc: string;
    marketing: string;
    marketingDesc: string;
    save: string;
    alwaysActive: string;
  };
}

interface CookieConsentProps {
  labels: CookieConsentLabels;
  apiUrl?: string;
  sourceApp?: string;
}

export const CookieConsent = ({
  labels,
  apiUrl,
  sourceApp = "landing",
}: CookieConsentProps) => {
  const { consent, hasConsented, acceptAll, rejectNonEssential, saveCustom } =
    useCookieConsent({ apiUrl, sourceApp });
  const [showSettings, setShowSettings] = useState(false);

  if (hasConsented) return null;

  const handleSaveCustom = (categories: Pick<ConsentState, CookieCategory>) => {
    saveCustom(categories);
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6"
      role="dialog"
      aria-label={labels.title}
      aria-modal="false"
    >
      <div
        className="max-w-2xl mx-auto rounded-2xl p-5 sm:p-6 shadow-[0_-8px_40px_rgba(0,0,0,0.25)]"
        style={{ background: "#15100C", border: "1px solid #2d2118" }}
      >
        <div className="flex items-start justify-between gap-4 mb-1">
          <h2
            className="text-[0.9375rem] font-medium"
            style={{ color: "#F8F4EE" }}
          >
            {labels.title}
          </h2>
        </div>

        <p
          className="text-[0.8125rem] leading-relaxed mb-4"
          style={{ color: "#8c7a6b" }}
        >
          {labels.description}
        </p>

        {!showSettings && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <button
              type="button"
              onClick={acceptAll}
              className="h-10 px-5 rounded-full text-[0.8125rem] font-medium transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
              style={{ background: "#a87640", color: "#F8F4EE" }}
            >
              {labels.acceptAll}
            </button>
            <button
              type="button"
              onClick={rejectNonEssential}
              className="h-10 px-5 rounded-full text-[0.8125rem] font-medium transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
              style={{
                background: "transparent",
                color: "#c2b5a8",
                border: "1px solid #3d2f24",
              }}
            >
              {labels.rejectAll}
            </button>
            <button
              type="button"
              onClick={() => setShowSettings(true)}
              className="h-10 px-4 text-[0.8125rem] font-medium transition-colors duration-200"
              style={{ color: "#a87640", background: "transparent" }}
            >
              {labels.customize}
            </button>
          </div>
        )}

        {showSettings && (
          <CookieConsentSettings
            labels={labels.settings}
            onSave={handleSaveCustom}
            initialState={consent}
          />
        )}
      </div>
    </div>
  );
};
